import { useCallback, useRef, useState } from 'react';
import { FileMetadata } from '../components/FileDropZone';

interface UseBrowserUploadOptions {
  /** Accepted file types (MIME types) */
  acceptedFileTypes: string[];
  /** Maximum number of files (default: 10) */
  maxFiles?: number;
  /** Maximum file size in bytes (default: 5MB) */
  maxFileSize?: number;
  /** Callback when files are ready for upload */
  onFilesReady?: (files: FileMetadata[]) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
}

interface UseBrowserUploadReturn {
  /** Function to trigger the browser file dialog */
  openFileDialog: () => void;
  /** Whether files are currently being processed */
  isProcessing: boolean;
  /** Any error that occurred during processing */
  error: string | null;
}

/**
 * Hook for browser-based file upload functionality
 * 
 * Provides a clean interface for opening the browser file dialog,
 * processing selected files, and converting them to FileWithPreview objects.
 */
export const useBrowserUpload = ({
  acceptedFileTypes,
  maxFiles = 10,
  maxFileSize = 5 * 1024 * 1024, // 5MB
  onFilesReady,
  onError
}: UseBrowserUploadOptions): UseBrowserUploadReturn => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Checks if a file type is accepted
   */
  const isFileTypeAccepted = useCallback((fileType: string): boolean => {
    if (!fileType || typeof fileType !== 'string') return false;
    
    return acceptedFileTypes
      .filter(acceptedType => acceptedType && typeof acceptedType === 'string')
      .some(acceptedType => {
        if (acceptedType.includes('*')) {
          const baseType = acceptedType.split('/')[0];
          return fileType.startsWith(baseType + '/');
        }
        return fileType === acceptedType;
      });
  }, [acceptedFileTypes]);

  /**
   * Validates a single file
   */
  const validateFile = useCallback((file: File): string | null => {
    // Check if file exists
    if (!file) {
      return 'Ungültige Datei';
    }

    // Check file size
    if (typeof file.size !== 'number' || file.size > maxFileSize) {
      const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
      };
      return `Datei zu groß (max. ${formatFileSize(maxFileSize)})`;
    }

    // Check file type
    if (!file.type || !isFileTypeAccepted(file.type)) {
      return `Dateityp nicht unterstützt`;
    }

    // Check file name
    if (!file.name || typeof file.name !== 'string' || file.name.trim().length === 0) {
      return `Dateiname ist erforderlich`;
    }

    if (file.name.length > 255) {
      return `Dateiname zu lang (max. 255 Zeichen)`;
    }

    return null;
  }, [isFileTypeAccepted, maxFileSize]);

  /**
   * Generates preview URL for image files
   */
  const generatePreview = useCallback((file: File): Promise<string | undefined> => {
    return new Promise((resolve, reject) => {
      if (!file || !file.type || !file.type.startsWith('image/')) {
        resolve(undefined);
        return;
      }

      // Skip preview generation for HEIC/HEIF as browsers don't support it natively
      if (
        file.type === 'image/heic' || 
        file.type === 'image/heif' || 
        file.name.toLowerCase().endsWith('.heic') || 
        file.name.toLowerCase().endsWith('.heif')
      ) {
        resolve(undefined);
        return;
      }

      try {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const result = e.target?.result as string;
          if (result) {
            resolve(result);
          } else {
            reject(new Error('Fehler beim Laden der Bildvorschau'));
          }
        };
        
        reader.onerror = () => {
          reject(new Error('Fehler beim Lesen der Datei'));
        };
        
        reader.readAsDataURL(file);
      } catch (error) {
        reject(error);
      }
    });
  }, []);

  /**
   * Filters files to only include supported types
   */
  const filterValidFiles = useCallback((files: FileList): File[] => {
    return Array.from(files).filter(file => 
      isFileTypeAccepted(file.type)
    );
  }, [isFileTypeAccepted]);

  /**
   * Handle file input change
   */
  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsProcessing(true);
    setError(null);

    try {
      if (files.length > maxFiles) {
        const errorMessage = `Zu viele Dateien. Maximum: ${maxFiles}`;
        setError(errorMessage);
        onError?.(errorMessage);
        setIsProcessing(false);
        return;
      }

      const validFiles = filterValidFiles(files);
      const invalidCount = files.length - validFiles.length;

      if (invalidCount > 0) {
        onError?.(`${invalidCount} Datei(en) wurden übersprungen (ungültiger Typ oder zu groß)`);
      }

      if (validFiles.length === 0) {
        const errorMessage = "Keine gültigen Dateien gefunden";
        setError(errorMessage);
        onError?.(errorMessage);
        setIsProcessing(false);
        return;
      }

      const filesWithMetadata: FileMetadata[] = [];
      
      for (const file of validFiles) {
        try {
          const validationError = validateFile(file);
          let preview: string | undefined;
          
          try {
            preview = await generatePreview(file);
          } catch (previewError) {
            console.warn('Preview generation failed:', previewError);
          }

          // Create FileMetadata object keeping the original File object separate
          const fileMetadata: FileMetadata = {
            id: Math.random().toString(36).substr(2, 9),
            file: file,
            preview: preview,
            displayName: file.name || 'untitled',
            error: validationError || undefined
          };
          
          filesWithMetadata.push(fileMetadata);
        } catch (error) {
          console.error('Error processing file:', file.name, error);
          onError?.(`Fehler beim Verarbeiten von ${file.name}`);
        }
      }

      if (filesWithMetadata.length === 0) {
        setError("Keine Dateien konnten verarbeitet werden");
        setIsProcessing(false);
        return;
      }

      // Call the callback to pass files to parent component
      if (onFilesReady) {
        onFilesReady(filesWithMetadata);
      }
            
    } catch (error) {
      console.error('File input handling error:', error);
      const errorMessage = "Fehler beim Verarbeiten der Dateien";
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
      // Reset file input
      if (e.target) {
        e.target.value = '';
      }
    }
  }, [maxFiles, filterValidFiles, onFilesReady, onError, validateFile, generatePreview]);

  /**
   * Opens the browser file dialog
   */
  const openFileDialog = useCallback(() => {
    try {
      // Create a temporary file input if it doesn't exist
      if (!fileInputRef.current) {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = acceptedFileTypes.join(',');
        input.style.display = 'none';
        
        input.addEventListener('change', (e) => {
          handleFileInputChange(e as any);
          // Remove the temporary input after use
          document.body.removeChild(input);
        });
        
        document.body.appendChild(input);
        input.click();
      } else {
        fileInputRef.current.click();
      }
    } catch (error) {
      console.error('Error opening file dialog:', error);
      const errorMessage = 'Fehler beim Öffnen des Datei-Dialogs';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [acceptedFileTypes, handleFileInputChange, onError]);

  return {
    openFileDialog,
    isProcessing,
    error
  };
};